import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { ExternalInlineLink } from '@client/components/Common/Link';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { selectOrg, useAuthStore } from '@client/stores/AuthStore';
import { trpcOptions } from '@client/trpc/client';
import { AuthError, cancelScheduledLoginRedirect } from '@client/utils/auth/authErrorHandler';
import { TokenRefreshManager } from '@client/utils/auth/TokenRefreshManager';
import { isNetworkError } from '@client/utils/errors/network-error-detection';
import { SYSTEM_ORG_IDS } from '@shared/constants';
import { TRIAL_EXPIRED_MESSAGE } from '@shared/utils/business/lite-trial';
import { skipToken, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Result } from 'antd';
import { LogOut } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';

const searchSchema = z.object({
    // The org whose trial refused the user, when the sender knew it. A value that does not read as
    // an id counts as unknown rather than failing the route.
    orgId: z.coerce.bigint().optional().catch(undefined),
});

export const Route = createFileRoute('/(authentication)/trial-expired')({
    validateSearch: zodValidator(searchSchema),
    component: TrialExpired,
});

/** Network and temporary Auth0 failures leave the screen for a later reload; anything else needs a sign-in. */
const isRetryableAuthFailure = (error: unknown): boolean =>
    error instanceof AuthError
        ? error.type === 'network_error' || error.type === 'temporary_auth_error'
        : isNetworkError(error);

const SUPPORT_EMAIL = 'support@getcurium.com';
// The shared message names the support address once; it becomes a mail link on this screen.
const [MESSAGE_BEFORE_EMAIL = TRIAL_EXPIRED_MESSAGE, MESSAGE_AFTER_EMAIL = ''] =
    TRIAL_EXPIRED_MESSAGE.split(SUPPORT_EMAIL);

function TrialExpired() {
    const navigate = useNavigate();
    const { orgId: refusedOrgId } = Route.useSearch();
    const storedOrgId = useAuthStore(selectOrg)?.id;
    const accessToken = useAuthStore((state) => state.accessToken);
    const getAccessToken = useAuthStore((state) => state.getAccessToken);
    const setAuthToken = useAuthStore((state) => state.setAuthToken);
    const clearAuthToken = useAuthStore((state) => state.clearAuthToken);
    const login = useAuthStore((state) => state.login);
    const logout = useAuthStore((state) => state.logout);

    // This screen sits outside RequireAuth, so neither its token renewal nor the boot warm-up runs
    // here, and a user waiting for staff can outlive the stored token. A request on an expired token
    // would leave this public pathname without one and fail silently. Renew the session once before
    // asking anything; when it cannot be renewed, sign in again for the refused org, which lands in
    // it or back on this screen with a fresh token. A connection or temporary Auth0 failure leaves
    // the screen for a later reload.
    const [sessionToken, setSessionToken] = useState<string>();
    const renewalTried = useRef(false);
    useEffect(() => {
        if (renewalTried.current) return;
        renewalTried.current = true;
        getAccessToken({ minValidityMs: TokenRefreshManager.REFRESH_BUFFER_MS, silent: true })
            .then((token) => {
                if (token !== accessToken) setAuthToken(token);
                setSessionToken(token);
            })
            .catch((error: unknown) => {
                if (isRetryableAuthFailure(error)) return;
                // The fallback sign-in `getAccessToken` schedules would return here without the org.
                cancelScheduledLoginRedirect();
                clearAuthToken();
                navigate({
                    to: '/login',
                    search: refusedOrgId !== undefined ? { orgId: refusedOrgId } : {},
                    replace: true,
                });
            });
    }, [accessToken, clearAuthToken, getAccessToken, navigate, refusedOrgId, setAuthToken]);

    // Once staff extend the trial, the org answers again: reloading this screen takes the user back
    // into the app. When the refused org is the stored one, probing it is enough. While the trial is
    // still ended the probe fails with `trial_expired`, which the global handler leaves alone on
    // this pathname, and the screen stays.
    const probedOrgId =
        sessionToken !== undefined && refusedOrgId !== undefined && refusedOrgId === storedOrgId
            ? storedOrgId
            : undefined;
    const probe = useQuery(
        trpcOptions.org.getOrg.queryOptions(probedOrgId === undefined ? skipToken : { orgId: probedOrgId }, {
            retry: false,
            meta: { errorSurface: 'silent', captureToSentry: false },
        }),
    );

    // A refused sign-in or org switch leaves the previous org stored, or none, so a probe would ask
    // the wrong org. The renewed token re-runs the bootstrap once instead, for the refused org when
    // it is known; a refusal or error leaves the screen as it is.
    const [bootstrappedOrgId, setBootstrappedOrgId] = useState<bigint>();
    const bootstrapTried = useRef(false);
    useEffect(() => {
        if (sessionToken === undefined || bootstrapTried.current) return;
        if (refusedOrgId !== undefined && refusedOrgId === storedOrgId) return;
        bootstrapTried.current = true;
        login(sessionToken, refusedOrgId !== undefined ? { requestedOrgId: refusedOrgId } : undefined)
            .then((result) => {
                if (result.ok) setBootstrappedOrgId(result.orgId);
            })
            .catch(() => undefined);
    }, [login, refusedOrgId, sessionToken, storedOrgId]);

    const restoredOrgId = (probe.isSuccess ? probedOrgId : undefined) ?? bootstrappedOrgId;
    useEffect(() => {
        if (restoredOrgId === undefined) return;
        if (restoredOrgId === SYSTEM_ORG_IDS.SYSTEM_CONSOLE) {
            navigate({ to: '/admin-console', replace: true });
        } else {
            navigate({ to: '/console/$orgId', params: { orgId: restoredOrgId }, replace: true });
        }
    }, [navigate, restoredOrgId]);

    return (
        <PageController route={Route}>
            <div className="grid h-screen w-full place-items-center bg-background">
                <Result
                    status="warning"
                    title="Trial expired"
                    subTitle={
                        <div className="text-center">
                            <p>
                                {MESSAGE_BEFORE_EMAIL}
                                <ExternalInlineLink href={`mailto:${SUPPORT_EMAIL}`} target="_self">
                                    {SUPPORT_EMAIL}
                                </ExternalInlineLink>
                                {MESSAGE_AFTER_EMAIL}
                            </p>
                            <div className="mt-6">
                                <Button type="secondary" icon={<LogOut />} onClick={() => logout('trial_expired')}>
                                    Sign out
                                </Button>
                            </div>
                        </div>
                    }
                    className="mx-auto w-full max-w-lg rounded-lg border border-separator bg-card p-8"
                />
            </div>
        </PageController>
    );
}
